import { useState, useEffect, useRef } from 'react';
import { MyProfileSection } from './sections/MyProfileSection';
import { CompanyInfoSection } from './sections/CompanyInfoSection';
import { SubscriptionSection } from './sections/SubscriptionSection';
import { PaymentMethodSection } from './sections/PaymentMethodSection';
import { InvoiceHistorySection } from './sections/InvoiceHistorySection';
import { ActiveModulesSection } from './sections/ActiveModulesSection';
import { NotificationsSection } from './sections/NotificationsSection';
import { DangerZoneSection } from './sections/DangerZoneSection';
import { PageHeader } from '../ui';
import '../../css/SettingsBillingPage.css';

type SectionId = 'profile' | 'company' | 'subscription' | 'payment' | 'invoices' | 'modules' | 'notifications';

const NAV_ITEMS: { id: SectionId; label: string }[] = [
    { id: 'profile', label: 'My Profile' },
    { id: 'company', label: 'Company Info' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'payment', label: 'Payment Method' },
    { id: 'invoices', label: 'Invoices' },
    { id: 'modules', label: 'Active Modules' },
    { id: 'notifications', label: 'Notifications' },
];

export const SettingsBillingPage = () => {
    const [activeSection, setActiveSection] = useState<SectionId>('profile');
    const sectionRefs = useRef<Record<SectionId, HTMLElement | null>>({
        profile: null,
        company: null,
        subscription: null,
        payment: null,
        invoices: null,
        modules: null,
        notifications: null,
    });

    const handleNavClick = (sectionId: SectionId) => {
        setActiveSection(sectionId);
        const el = sectionRefs.current[sectionId];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Update active section based on scroll position
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const id = entry.target.getAttribute('data-section-id') as SectionId;
                        if (id) setActiveSection(id);
                    }
                }
            },
            { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
        );

        Object.values(sectionRefs.current).forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, []);

    return (
        <div className="page-container settings-page">
            <PageHeader
                title="Settings & Billing"
                subtitle="Manage your profile, company information, subscription, and payment methods."
            />

            <div className="settings-page-layout">
                {/* Sticky Sub Navigation */}
                <nav className="settings-page-nav" aria-label="Settings sections">
                    {NAV_ITEMS.map((item) => (
                        <button
                            key={item.id}
                            className={`settings-nav-item ${activeSection === item.id ? 'settings-nav-item--active' : ''}`}
                            onClick={() => handleNavClick(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </nav>

                {/* Content */}
                <div className="settings-page-content">
                    <div
                        ref={(el) => {
                            sectionRefs.current.profile = el;
                        }}
                        data-section-id="profile"
                    >
                        <MyProfileSection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.company = el;
                        }}
                        data-section-id="company"
                    >
                        <CompanyInfoSection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.subscription = el;
                        }}
                        data-section-id="subscription"
                    >
                        <SubscriptionSection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.payment = el;
                        }}
                        data-section-id="payment"
                    >
                        <PaymentMethodSection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.invoices = el;
                        }}
                        data-section-id="invoices"
                    >
                        <InvoiceHistorySection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.modules = el;
                        }}
                        data-section-id="modules"
                    >
                        <ActiveModulesSection />
                    </div>

                    <div
                        ref={(el) => {
                            sectionRefs.current.notifications = el;
                        }}
                        data-section-id="notifications"
                    >
                        <NotificationsSection />
                    </div>

                    {/* Danger Zone (not in nav) */}
                    <DangerZoneSection />
                </div>
            </div>
        </div>
    );
};
